import { UseFormMethods } from 'react-hook-form';
import { UserRole, User as UserBase, Account } from '@adjust/dashboard-core/api/types';
import { SelectOption as ExternalSelectOption } from '@adjust/dashboard-components';

export enum Platform {
  android = 'android',
  ios = 'ios',
  multi = 'multi',
}

export enum Format {
  tdl = 'tdl',
  ulink = 'ulink',
  jsr = 'jsr',
}

export enum AdEnvironment {
  email = 'email',
  chromeBrowser = 'chrome-browser',
  organicFacebook = 'organic-facebook',
  organicInstagram = 'organic-instagram',
  safariBrowser = 'safari-browser',
  slack = 'slack',
  organicSnapchatChats = 'organic-snapchat-chats',
  organicTwitter = 'organic-twitter',
  telegram = 'telegram',
  other = 'other',
}

export enum RequestStatus {
  idle = 'idle',
  fetching = 'fetching',
  fetched = 'fetched',
  failed = 'failed',
}

export interface User extends UserBase {
  local_roles: {
    mobile_apps: {
      token: string;
      role: Exclude<UserRole, UserRole.Admin | UserRole.SuperAdmin>;
    }[];
  };
}

export interface App {
  app_token: string;
  name: string;
  platforms: {
    ios?: {
      configured: boolean;
      app_scheme?: string;
      short_universal_link_token?: string;
    };
    android?: {
      configured: boolean;
      app_scheme?: string;
    };
  };
  permissions: {
    update_settings: boolean;
  };
  subdomain: {
    domain: string;
    subdomain: string | null;
  } | null;
  data_residency_region: string | null;
}
export interface Tracker {
  token: string;
  label: string;
  app_token: string;
  settings: {
    cost_data_parameters?: {
      cost_id?: string;
      cost_type?: string;
      cost_amount?: string;
      cost_currency?: string;
    };
    campaign_structure?: {
      campaign: string;
      adgroup: string;
      creative: string;
    };
    tracking_parameters?: {
      [key: string]: string;
      idfa: string;
      gps_adid: string;
    };
  };
}

export interface SelectOption {
  label: string;
  value: string;
}

export interface AppOption extends SelectOption {
  platform: Platform;
  trackerOptions: TrackerOption[];
  detailsStatus: RequestStatus;
  rawUniversalLink?: string;
  iosAppScheme?: string;
  androidAppScheme?: string;
  dataResidency?: string;
}

export interface TrackerOption extends SelectOption {
  settings: {
    campaign?: string;
    adgroup?: string;
    creative?: string;
    idfa?: string;
    gpsAdid?: string;
    label?: string;
  };
  additionalParameters?: { [key: string]: string | undefined };
}

export interface Link {
  id: number;
  title?: string;
  url: string;
  ad_environment?: AdEnvironment;
  archived_at?: string;
  isInvalid?: boolean;
  verification_required?: boolean;
}

export interface LegacyLink extends Omit<Link, 'id' | 'url' | 'archived_at' | 'ad_environment'> {
  link: string;
  isArchived?: boolean;
  adEnvironment?: AdEnvironment;
  isMigrated?: boolean;
  format?: Format;
}

export interface LinkStorage {
  key: string;
  getList: () => (Omit<Link, 'id'> & { legacyIndex: number })[];
  markAsMigrated: (index: number) => void;
}

export interface UseLinks {
  fetch: () => Promise<Link[]>;
  migrateData: (user: User) => Promise<void>;
  list: Link[];
  create: (link: Omit<Link, 'id'>) => Promise<void>;
  update: (id: number, link: Partial<Link> & { archived?: boolean }) => Promise<void>;
  highlightedId?: number;
  processingId?: number;
  remove: (id: number) => void;
  edit: (id: number) => void;
  editFields: LinkFields | undefined;
  skipEdit: () => void;
  isInvalid: boolean[];
  isLoading: boolean;
  isArchivedVisible: boolean;
  toggleIsArchivedVisible: () => void;
}

export interface LinkFields {
  platform: Platform;
  format: Format;
  adEnvironment?: AdEnvironment;
  dataResidency?: string;

  targetApp?: string;
  targetAppPlatform?: Platform;
  targetTracker?: string;
  targetTrackerManual?: boolean;
  androidApp?: string;
  androidTracker?: string;
  androidTrackerManual?: boolean;

  rawUniversalLink?: string;
  iosAppScheme?: string;
  androidAppScheme?: string;
  commonAppScheme?: string;

  iosUniversalLinkPath?: string;
  iosUniversalLinkPathEnforce?: boolean;
  androidDeeplinkPath?: string;
  androidDeeplinkPathEnforce?: boolean;
  androidDeeplinkPathSameAsIos?: boolean;
  commonDeeplinkPath?: string;
  commonDeeplinkPathEnforce?: boolean;

  campaign?: string;
  adgroup?: string;
  creative?: string;

  label?: string;
  fallbackUrl?: string;
  redirectUrl?: string;
  redirectMacos?: string;
  redirectIos?: string;
  redirectAndroid?: string;
  idfa?: string;
  gpsAdid?: string;
  id?: number;
}

export interface UseLinkForm {
  form: UseFormMethods<LinkFields>;
  platform: Platform;
  format: Format;
  formats: SelectOption[];
  targetAppsOptions: AppOption[];
  androidAppsOptions: AppOption[];
  adEnvironment?: AdEnvironment;
  targetAppOption?: AppOption;
  targetTrackerOption?: TrackerOption;
  androidAppOption?: AppOption;
  iosUniversalLinkPath?: string;
  androidDeeplinkPathSameAsIos?: boolean;
}

export interface FormatLinkFields extends LinkFields {
  trackerToken: string;
  androidAppSchemePrefilled?: boolean;
  additionalParameters?: { [key: string]: string | undefined };
}

export interface TrackEvent {
  (name: string, data?: unknown): void;
}

export interface UseConfirmParams {
  successMessage: string;
  onConfirm: () => void;
}

export interface UseConfirm {
  params: UseConfirmParams | undefined;
  isSuccess: boolean;
  open: (params: UseConfirmParams) => void;
  close: () => void;
  confirm: () => void;
}

export interface UseApps {
  options: AppOption[];
  fetchDetails: (token: string) => Promise<AppOption | void>;
  fetch: () => Promise<void>;
  setAvailableTokens: (tokens: string[]) => void;
}

export interface UseCurrentUser {
  user?: User;
  account?: Account;
  accounts: Account[];
  fetch: () => Promise<{ user: User; account: Account }>;
  logout: () => void;
  switchAccount: (accountId: number) => void;
  searchAccount: (query: string) => Promise<ExternalSelectOption[]>;
}
